import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  IUserGetDetailsReq,
  IUserGetLavandierReq,
  IUserPutAnonymiseCancelReq,
  IUserPutDisableReq,
  IUserPutPasswordReq,
  IUserPutReq,
  IUserDeleteReq,
  TUserGetDetailsRep,
  TUserGetLavandierRep,
  TUserPutAnonymiseCancelRep,
  TUserPutDisableRep,
  TUserPutPasswordRep,
  TUserPutRep,
  TUserDeleteRep
} from '../../lib-shared/api/client/User.Type';
import {
  IPaymentGetPaymentReq, IPaymentGetOrderIdReq,
  IPaymentPostSetupIntentReq,
  IPaymentPutCardDeleteReq,
  TPaymentGetPaymentRep, TPaymentGetOrderIdRep,
  TPaymentPostSetupIntentRep,
  TPaymentPutCardDeleteRep
} from '../../lib-shared/api/client/Payment.Type';
import {
  ISubscriptionGetDetailsReq,
  ISubscriptionPostReq,
  TSubscriptionGetDetailsRep,
  TSubscriptionPostRep
} from '../../lib-shared/api/client/Subscription.Type';
import {
  IOrderKitDeleteIdReq,
  IOrderKitGetShipLaterReq,
  IOrderKitPostOrderReq, TOrderKitDeleteIdRep, TOrderKitGetShipLaterRep,
  TOrderKitPostOrderRep
} from '../../lib-shared/api/client/OrderKit.Type';
import {
  TOrderGetIdDetailsReq,
  TOrderGetIdDetailsRep, TOrderGetRep,
  TOrderGetReq, IOrderPutIdPayReq, TOrderPutIdPayRep, IOrderPutIdHouseReq, TOrderPutIdHouseRep, TOrderGetHouseNameRep,
  IOrderGetHouseNameReq
} from '../../lib-shared/api/client/Order.Type';
import {
  TTransportSheetGetIsAvailableRep,
  ITransportSheetGetIsAvailableReq,
  TTransportSheetGetRep,
  TTransportSheetGetReq,
  TTransportSheetPostPdfRep,
  ITransportSheetPostPdfReq,
  TTransportSheetPutBookRep,
  ITransportSheetPutBookReq, ITransportSheetPutIdPdfReq, TTransportSheetPutIdPdfRep, ITransportSheetPutIdHouseReq, TTransportSheetPutIdHouseRep,
} from '../../lib-shared/api/client/TransportSheet.Type';
import { IOrderLoyaltyGetReq, TOrderLoyaltyGetRep } from '../../lib-shared/api/client/OrderLoyalty.Type';
import { IContactPostReq, TContactPostRep } from '../../lib-shared/api/client/Contact.Type';
import { ICreditGetReq, TCreditGetRep } from '../../lib-shared/api/client/Credit.Type';
import { IReferralGetReq, IReferralPostReq, TReferralGetRep, TReferralPostRep } from '../../lib-shared/api/client/Referral.Type';
import { IDiscountCodeGetReq, TDiscountCodeGetRep } from '../../lib-shared/api/client/DiscountCode.Type';
import {
  IHouseDeleteIdReq, IHouseGetReq,
  IHousePostReq,
  IHousePutIdDefaultReq, IHousePutIdReq, THouseDeleteIdRep, THouseGetRep,
  THousePostRep,
  THousePutIdDefaultRep, THousePutIdRep,
} from '../../lib-shared/api/client/House.Type';
import { TPreOrderPutReq, TPreOrderPutRep } from '../../lib-shared/api/client/PreOrder.Type';
import {
  IArticleGetDetailsReq, IArticleGetFamilyReq, IArticleGetOptionReq, TArticleGetDetailsRep, TArticleGetFamilyRep,
  TArticleGetOptionRep
} from '../../lib-shared/api/client/Article.Type';
import { IArticlePackGetReq, TArticlePackGetRep } from '../../lib-shared/api/client/ArticlePack.Type';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private url = ApiService.generateUrl(environment.api_url);

  constructor(
    private authService: AuthService,
    private http: HttpClient
  ) {
  }

  static generateUrl(base: string) {
    return `${base}/client`;
  }

  setUrl(base: string) {
    this.url = ApiService.generateUrl(base);
  }

  putUserAnonymiseCancel(params: IUserPutAnonymiseCancelReq) {
    return this.http.put<TUserPutAnonymiseCancelRep>(`${this.url}/user/anonymise/cancel`, params, this.authService.getHttpOptions());
  }

  putPassword(params: IUserPutPasswordReq) {
    return this.http.put<TUserPutPasswordRep>(`${this.url}/user/password`, params, this.authService.getHttpOptions());
  }

  putUserDisable(params: IUserPutDisableReq) {
    return this.http.put<TUserPutDisableRep>(`${this.url}/user/disable`, params, this.authService.getHttpOptions());
  }

  putUser(user: IUserPutReq) {
    return this.http.put<TUserPutRep>(`${this.url}/user`, user, this.authService.getHttpOptions());
  }

  getUserLavandier(params: IUserGetLavandierReq) {
    return this.http.get<TUserGetLavandierRep>(`${this.url}/user/lavandier`, this.authService.getHttpOptions());
  }

  getUserDetails(params: IUserGetDetailsReq) {
    return this.http.get<TUserGetDetailsRep>(`${this.url}/user/details`, this.authService.getHttpOptions());
  }

  deleteUser(params: IUserDeleteReq) {
    return this.http.delete<TUserDeleteRep>(`${this.url}/user`, this.authService.getHttpOptions());
  }

  postTransportSheetPdf(params: ITransportSheetPostPdfReq): Observable<Blob & TTransportSheetPostPdfRep> {
    return this.http.post(`${this.url}/transportsheet/pdf`, params, {
      headers: this.authService.getHttpHeaders(),
      responseType: 'blob',
    });
  }

  putTransportSheetBook(params: ITransportSheetPutBookReq) {
    return this.http.put<TTransportSheetPutBookRep>(`${this.url}/transportsheet/book`, params, this.authService.getHttpOptions());
  }

  putTransportSheetIdHouse(id: number, params: ITransportSheetPutIdHouseReq) {
    return this.http.put<TTransportSheetPutIdHouseRep>(`${this.url}/transportsheet/${id}/house`, params, this.authService.getHttpOptions());
  }

  putTransportSheetIdPdf(id: number, params: ITransportSheetPutIdPdfReq): Observable<Blob & TTransportSheetPutIdPdfRep> {
    return this.http.put(`${this.url}/transportsheet/${id}/pdf`, params, {
      headers: this.authService.getHttpHeaders(),
      responseType: 'blob',
    });
  }

  getTransportSheetIsAvailable(params: ITransportSheetGetIsAvailableReq) {
    return this.http.get<TTransportSheetGetIsAvailableRep>(`${this.url}/transportsheet/isavailable`, {
      headers: this.authService.getHttpHeaders(),
      // as any is here for retro compatibility with old version of angular
      params: new HttpParams({fromObject: params as any}),
    });
  }

  getTransportSheet(params: TTransportSheetGetReq) {
    return this.http.get<TTransportSheetGetRep>(`${this.url}/transportsheet`, this.authService.getHttpOptions());
  }

  postHouse(params: IHousePostReq) {
    return this.http.post<THousePostRep>(`${this.url}/house`, params, this.authService.getHttpOptions());
  }

  putHouseIdDefault(id: number, params: IHousePutIdDefaultReq) {
    return this.http.put<THousePutIdDefaultRep>(`${this.url}/house/${id}/default`, null, this.authService.getHttpOptions());
  }

  putHouseId(houseId: number, params: IHousePutIdReq) {
    return this.http.put<THousePutIdRep>(`${this.url}/house/${houseId}`, params, this.authService.getHttpOptions());
  }

  getHouse(params: IHouseGetReq) {
    return this.http.get<THouseGetRep>(`${this.url}/house`, this.authService.getHttpOptions());
  }

  deleteHouseId(houseId: number, params: IHouseDeleteIdReq) {
    return this.http.delete<THouseDeleteIdRep>(`${this.url}/house/${houseId}`, this.authService.getHttpOptions());
  }

  putOrderIdHouse(id: number, params: IOrderPutIdHouseReq) {
    return this.http.put<TOrderPutIdHouseRep>(`${this.url}/order/${id}/house`, params, this.authService.getHttpOptions());
  }

  putOrderIdPay(id: number, params: IOrderPutIdPayReq) {
    return this.http.put<TOrderPutIdPayRep>(`${this.url}/order/${id}/pay`, params, this.authService.getHttpOptions());
  }

  getOrderHouseNames(params: IOrderGetHouseNameReq) {
    return this.http.get<TOrderGetHouseNameRep>(`${this.url}/order/housename`, this.authService.getHttpOptions());
  }

  getOrderDetails(id: number, params: TOrderGetIdDetailsReq) {
    return this.http.get<TOrderGetIdDetailsRep>(`${this.url}/order/${id}/details`, this.authService.getHttpOptions());
  }

  postOrderKitOrder(params: IOrderKitPostOrderReq) {
    return this.http.post<TOrderKitPostOrderRep>(`${this.url}/orderkit/order`, params, this.authService.getHttpOptions());
  }

  getOrderKitShipLater(params: IOrderKitGetShipLaterReq) {
    return this.http.get<TOrderKitGetShipLaterRep>(`${this.url}/orderkit/shiplater`, this.authService.getHttpOptions());
  }

  deleteOrderKitShipLater(id: number, params: IOrderKitDeleteIdReq) {
    return this.http.delete<TOrderKitDeleteIdRep>(`${this.url}/orderkit/${id}`, this.authService.getHttpOptions());
  }

  getOrderLoyalty(params: IOrderLoyaltyGetReq) {
    return this.http.get<TOrderLoyaltyGetRep>(`${this.url}/orderloyalty`, this.authService.getHttpOptions());
  }

  getOrderList(params: TOrderGetReq) {
    return this.http.get<TOrderGetRep>(`${this.url}/order`, {
      headers: this.authService.getHttpHeaders(),
      // as any is here for retro compatibility with old version of angular
      params: new HttpParams({fromObject: params as any}),
    });
  }

  getArticleFamilyList(params: IArticleGetFamilyReq) {
    return this.http.get<TArticleGetFamilyRep>(`${this.url}/article/family`, this.authService.getHttpOptions());
  }

  getArticleDetailsList(params: IArticleGetDetailsReq) {
    return this.http.get<TArticleGetDetailsRep>(`${this.url}/article/details`, this.authService.getHttpOptions());
  }

  getArticleOptionList(params: IArticleGetOptionReq) {
    return this.http.get<TArticleGetOptionRep>(`${this.url}/article/option`, this.authService.getHttpOptions());
  }

  getArticlePack(params: IArticlePackGetReq) {
    return this.http.get<TArticlePackGetRep>(`${this.url}/articlepack`, this.authService.getHttpOptions());
  }

  postContact(data: IContactPostReq) {
    return this.http.post<TContactPostRep>(`${this.url}/contact`, data, this.authService.getHttpOptions());
  }

  getDiscountCode(params: IDiscountCodeGetReq) {
    return this.http.get<TDiscountCodeGetRep>(`${this.url}/discountcode`, {
      headers: this.authService.getHttpHeaders(),
      // as any is here for retro compatibility with old version of angular
      params: new HttpParams({fromObject: params as any}),
    });
  }

  postPaymentSetupIntent(params: IPaymentPostSetupIntentReq) {
    return this.http.post<TPaymentPostSetupIntentRep>(`${this.url}/payment/setupintent`, params, this.authService.getHttpOptions());
  }

  putPaymentCardDelete(params: IPaymentPutCardDeleteReq) {
    return this.http.put<TPaymentPutCardDeleteRep>(`${this.url}/payment/card/delete`, params, this.authService.getHttpOptions());
  }

  getPaymentOrderId(orderId: number, params: IPaymentGetOrderIdReq) {
    return this.http.get<TPaymentGetOrderIdRep>(`${this.url}/payment/order/${orderId}`, this.authService.getHttpOptions());
  }

  getPaymentPaymentList(params: IPaymentGetPaymentReq) {
    return this.http.get<TPaymentGetPaymentRep>(`${this.url}/payment/payment`, this.authService.getHttpOptions());
  }

  getDocInvoice(orderId: number) {
    return this.http.get(`${this.url}/doc/invoice/${orderId}`, {
      headers: this.authService.getHttpHeaders(), responseType: 'blob'
    });
  }

  putPreOrder(params: TPreOrderPutReq) {
    return this.http.put<TPreOrderPutRep>(`${this.url}/preorder`, params, this.authService.getHttpOptions());
  }

  getCredit(params: ICreditGetReq) {
    return this.http.get<TCreditGetRep>(`${this.url}/credit`, this.authService.getHttpOptions());
  }

  postReferral(params: IReferralPostReq) {
    return this.http.post<TReferralPostRep>(`${this.url}/referral`, params, this.authService.getHttpOptions());
  }

  getReferral(params: IReferralGetReq) {
    return this.http.get<TReferralGetRep>(`${this.url}/referral`, this.authService.getHttpOptions());
  }

  postSubscription(params: ISubscriptionPostReq) {
    return this.http.post<TSubscriptionPostRep>(`${this.url}/subscription`, params, this.authService.getHttpOptions());
  }

  getSubscriptionDetails(params: ISubscriptionGetDetailsReq) {
    return this.http.get<TSubscriptionGetDetailsRep>(`${this.url}/subscription/details`, this.authService.getHttpOptions());
  }
}
